import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProfileQuery } from '@entities/Profile';
import { paths } from '@shared/config';
import { campaignSearch } from '@shared/lib/campaignParams';
import type { AuthStage } from '@shared/types';
import { useAuthStore } from '../model/useAuthStore';
import { AfterSignInRedirect } from './AfterSignInRedirect';
import { FullScreenLoader } from './FullScreenLoader';

interface OnboardingGateProps {
  children: ReactNode;
}

const stagePath: Record<AuthStage, string> = {
  auth: paths.auth,
  onboarding: paths.onboarding,
  app: paths.dashboard,
};

export function OnboardingGate({ children }: OnboardingGateProps) {
  const location = useLocation();
  const stage = useAuthStore((s) => s.stage);
  const setStage = useAuthStore((s) => s.setStage);
  const profileQuery = useProfileQuery({ enabled: stage === 'onboarding' });
  const alreadyCompleted = profileQuery.data?.completed_onboarding === true;
  // Итог засчитывает знакомство сразу при входе (B5, 2026-10-20), но ученик ещё выбирает время
  // напоминаний: пока он на итоге, перечитанный профиль не уводит его на главную — уведёт «На главную».
  const onResult = location.pathname === paths.onboardingResult;
  const leave = alreadyCompleted && !onResult;

  useEffect(() => {
    if (leave && stage === 'onboarding') {
      setStage('app');
    }
  }, [leave, stage, setStage]);

  // Знакомство пройдено (или уже было) — туда, куда шёл до входа (returnTo), иначе на главную.
  if (stage === 'app') return <AfterSignInRedirect search={location.search} />;

  if (stage !== 'onboarding') {
    // Метки кампании переносим (см. campaignSearch), остальные параметры отбрасываем.
    return <Navigate to={{ pathname: stagePath[stage], search: campaignSearch(location.search) }} replace />;
  }

  if (leave) {
    return <FullScreenLoader />;
  }

  if (profileQuery.isPending) {
    return <FullScreenLoader />;
  }

  return <>{children}</>;
}
